import { FC, useMemo } from "react";
import styled from "styled-components";
import { mobile, tablet } from "components/templates/Breakpoints";
import { ResourceCard, ResourceCardProps } from "./ResourceCard";
import { H3 } from "components/atoms/Heading";
import { color, ContentType, localiseContentType } from "@utils";
import { BetterButton } from "components/atoms/BetterButton";
import { useBreakpoints, YuLifeLocale } from "@hooks";
import Link from "next/link";

interface ResourceCardsSectionProps {
  contentType?: ContentType;
  cards: ResourceCardProps[];
  locale: YuLifeLocale;
}

export const ResourceCardsSection: FC<ResourceCardsSectionProps> = ({
  cards,
  contentType,
  locale,
}) => {
  const { isMobile, isDesktop } = useBreakpoints();

  const convertContentTypeToSlug = (contentType: ContentType) => {
    switch (contentType) {
      case ContentType.caseStudy:
        return "case-studies";
      case ContentType.newsEvents:
        return "news-and-events";
      case ContentType.blog:
        return "blog";
      default:
        return contentType;
    }
  };

  const title = localiseContentType(contentType, locale);

  const linkText = `See all ${title}`;
  const href = `/${convertContentTypeToSlug(contentType)}`;

  return (
    <>
      {title && (
        <TitleContainer>
          <SectionTitle>{title}</SectionTitle>
          <LinkStyle color={color.P100} href={href}>
            {linkText}
          </LinkStyle>
        </TitleContainer>
      )}
      <FlexContainer>
        {cards.slice(0, 3).map((card, index) => (
          <FlexItem key={card.title + index}>
            <ResourceCard
              title={card.title}
              tag={card.tag}
              contentType={card.contentType}
              thumbnailSrc={card.thumbnailSrc}
              thumbnailAlt={card.thumbnailAlt}
              isHorizontal={!isDesktop}
              duration={!isMobile && card.duration}
              excerpt={card.excerpt}
              slug={card.slug}
              imgHeight={!isMobile && "12rem"}
            />
          </FlexItem>
        ))}
        {title && (
          <ButtonContainer>
            <Link href={href}>
              <BetterButton text={linkText} width="100%" />
            </Link>
          </ButtonContainer>
        )}
      </FlexContainer>
    </>
  );
};

const TitleContainer = styled.div`
  display: flex;
  padding-bottom: 2.5rem;
  justify-content: space-between;
  align-items: end;
`;

const SectionTitle = styled(H3)`
  font-size: 2.5rem;

  ${mobile} {
    font-size: 2rem;
  }
`;

const LinkStyle = styled(Link)`
  font-weight: 700;
  line-height: 1.5rem;
  letter-spacing: 0.075rem;
  color: ${color.P100};
  text-decoration: underline;

  ${mobile} {
    display: none;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 2rem;

  ${tablet} {
    flex-wrap: wrap;
  }
`;

const FlexItem = styled.div`
  flex-basis: 33%;

  ${tablet} {
    flex-basis: 100%;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: none;

  ${mobile} {
    display: block;
    margin: 0 auto;
    padding-top: 1rem;
  }
`;
